import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { 
  Bell, 
  Mail, 
  Clock, 
  AlertCircle, 
  CheckCircle2,
  FileText,
  Calendar,
  MessageCircle,
  Smartphone
} from "lucide-react";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { toast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface NotificacionConfig {
  id: string;
  nombre: string;
  descripcion: string;
  categoria: string;
  email: boolean;
  inApp: boolean;
  whatsapp: boolean;
  diasAnticipacion?: number;
}

const GestionNotificaciones = () => {
  const { status: pushStatus, requestPermissions, unregisterPushNotifications } = usePushNotifications();

  const [emailConfig, setEmailConfig] = useState({
    emailNotificaciones: "",
    resumenDiario: false,
    resumenSemanal: true,
    resumenMensual: false,
    horaEnvio: "09:00"
  });

  const [whatsappConfig, setWhatsappConfig] = useState({
    numeroWhatsapp: "",
    whatsappHabilitado: false
  });

  const [pushConfig, setPushConfig] = useState({
    pushHabilitado: false
  });

  const [notificaciones, setNotificaciones] = useState<NotificacionConfig[]>([
    // Vencimientos
    {
      id: "venc-proximo",
      nombre: "Vencimiento Próximo",
      descripcion: "Notificar cuando una obligación está por vencer",
      categoria: "Vencimientos",
      email: true,
      inApp: true,
      whatsapp: false,
      diasAnticipacion: 7
    },
    {
      id: "venc-hoy",
      nombre: "Vence Hoy",
      descripcion: "Notificar obligaciones que vencen hoy",
      categoria: "Vencimientos",
      email: true,
      inApp: true,
      whatsapp: false
    },
    {
      id: "venc-vencido",
      nombre: "Obligación Vencida",
      descripcion: "Notificar cuando una obligación se ha vencido",
      categoria: "Vencimientos",
      email: true,
      inApp: true,
      whatsapp: false
    },
    // Cumplimiento
    {
      id: "cump-nueva",
      nombre: "Nueva Obligación",
      descripcion: "Notificar cuando se crea una nueva obligación",
      categoria: "Cumplimiento",
      email: false,
      inApp: true,
      whatsapp: false
    },
    {
      id: "cump-actualizada",
      nombre: "Obligación Actualizada",
      descripcion: "Notificar cuando se actualiza una obligación",
      categoria: "Cumplimiento",
      email: false,
      inApp: true,
      whatsapp: false
    },
    {
      id: "cump-completada",
      nombre: "Obligación Completada",
      descripcion: "Notificar cuando se completa una obligación",
      categoria: "Cumplimiento",
      email: true,
      inApp: true,
      whatsapp: false
    },
    // Documentos
    {
      id: "doc-nuevo",
      nombre: "Nuevo Documento",
      descripcion: "Notificar cuando se sube un nuevo documento",
      categoria: "Documentos",
      email: false,
      inApp: true,
      whatsapp: false
    },
    {
      id: "doc-aprobado",
      nombre: "Documento Aprobado",
      descripcion: "Notificar cuando se aprueba un documento",
      categoria: "Documentos",
      email: true,
      inApp: true,
      whatsapp: false
    },
    {
      id: "doc-rechazado",
      nombre: "Documento Rechazado",
      descripcion: "Notificar cuando se rechaza un documento",
      categoria: "Documentos",
      email: true,
      inApp: true,
      whatsapp: false
    },
    // Sistema
    {
      id: "sys-backup",
      nombre: "Respaldo Completado",
      descripcion: "Notificar cuando se completa un respaldo del sistema",
      categoria: "Sistema",
      email: false,
      inApp: true,
      whatsapp: false
    },
    {
      id: "sys-error",
      nombre: "Error del Sistema",
      descripcion: "Notificar errores críticos del sistema",
      categoria: "Sistema",
      email: true,
      inApp: true,
      whatsapp: false
    }
  ]);

  // Cargar configuración guardada
  useEffect(() => {
    const savedEmailConfig = localStorage.getItem("emailConfig");
    const savedWhatsappConfig = localStorage.getItem("whatsappConfig");
    const savedPushConfig = localStorage.getItem("pushConfig");
    const savedNotificaciones = localStorage.getItem("notificaciones");

    if (savedEmailConfig) {
      setEmailConfig(JSON.parse(savedEmailConfig));
    }
    if (savedWhatsappConfig) {
      setWhatsappConfig(JSON.parse(savedWhatsappConfig));
    }
    if (savedPushConfig) {
      setPushConfig(JSON.parse(savedPushConfig));
    }
    if (savedNotificaciones) {
      setNotificaciones(JSON.parse(savedNotificaciones));
    }
  }, []);

  // Sincronizar estado de push con el hook
  useEffect(() => {
    if (pushStatus.isRegistered && !pushConfig.pushHabilitado) {
      setPushConfig({ pushHabilitado: true });
    }
  }, [pushStatus.isRegistered]);

  const guardarConfiguracion = () => {
    localStorage.setItem("emailConfig", JSON.stringify(emailConfig));
    localStorage.setItem("whatsappConfig", JSON.stringify(whatsappConfig));
    localStorage.setItem("pushConfig", JSON.stringify(pushConfig));
    localStorage.setItem("notificaciones", JSON.stringify(notificaciones));
    
    toast({
      title: "Configuración guardada",
      description: "Las preferencias de notificaciones se han actualizado correctamente.",
    });
  };

  const handlePushToggle = async (enabled: boolean) => {
    if (enabled) {
      if (!pushStatus.isSupported) {
        toast({
          title: "No disponible",
          description: "Las notificaciones push no están disponibles en este navegador o dispositivo.",
          variant: "destructive"
        });
        return;
      }
      await requestPermissions();
      setPushConfig({ pushHabilitado: true });
    } else {
      await unregisterPushNotifications();
      setPushConfig({ pushHabilitado: false });
    }
  };

  const toggleNotificacion = (id: string, tipo: 'email' | 'inApp' | 'whatsapp') => {
    setNotificaciones(prev => prev.map(notif => 
      notif.id === id ? { ...notif, [tipo]: !notif[tipo] } : notif
    ));
  };

  const actualizarDiasAnticipacion = (id: string, dias: number) => {
    setNotificaciones(prev => prev.map(notif => 
      notif.id === id ? { ...notif, diasAnticipacion: dias } : notif
    ));
  };

  const getCategoriaIcon = (categoria: string) => {
    switch (categoria) {
      case "Vencimientos": return <Clock className="h-4 w-4" />;
      case "Cumplimiento": return <CheckCircle2 className="h-4 w-4" />;
      case "Documentos": return <FileText className="h-4 w-4" />;
      case "Sistema": return <AlertCircle className="h-4 w-4" />;
      default: return <Bell className="h-4 w-4" />;
    }
  };

  const categoriasUnicas = Array.from(new Set(notificaciones.map(n => n.categoria)));

  return (
    <div className="space-y-6">
      {/* Configuración de Email */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-viable-blue/10 flex items-center justify-center">
              <Mail className="h-5 w-5 text-viable-blue" />
            </div>
            <div>
              <CardTitle>Configuración de Email</CardTitle>
              <CardDescription>Configura las notificaciones por correo electrónico</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email">Email para notificaciones</Label>
            <Input
              id="email"
              type="email"
              placeholder="correo@empresa.com"
              value={emailConfig.emailNotificaciones}
              onChange={(e) => setEmailConfig({ ...emailConfig, emailNotificaciones: e.target.value })}
            />
          </div>

          <Separator />

          <div className="space-y-4">
            <h4 className="text-sm font-medium">Resúmenes automáticos</h4>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="diario">Resumen Diario</Label>
                <p className="text-xs text-muted-foreground">Recibir un resumen al final de cada día</p>
              </div>
              <Switch
                id="diario"
                checked={emailConfig.resumenDiario}
                onCheckedChange={(checked) => setEmailConfig({ ...emailConfig, resumenDiario: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="semanal">Resumen Semanal</Label>
                <p className="text-xs text-muted-foreground">Recibir un resumen cada lunes</p>
              </div>
              <Switch
                id="semanal"
                checked={emailConfig.resumenSemanal}
                onCheckedChange={(checked) => setEmailConfig({ ...emailConfig, resumenSemanal: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="mensual">Resumen Mensual</Label>
                <p className="text-xs text-muted-foreground">Recibir un resumen el primer día del mes</p>
              </div>
              <Switch
                id="mensual"
                checked={emailConfig.resumenMensual}
                onCheckedChange={(checked) => setEmailConfig({ ...emailConfig, resumenMensual: checked })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hora">Hora de envío de resúmenes</Label>
              <Input
                id="hora"
                type="time"
                value={emailConfig.horaEnvio}
                onChange={(e) => setEmailConfig({ ...emailConfig, horaEnvio: e.target.value })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configuración de Push Notifications */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-viable-orange/10 flex items-center justify-center">
              <Smartphone className="h-5 w-5 text-viable-orange" />
            </div>
            <div className="flex-1">
              <CardTitle>Notificaciones Push</CardTitle>
              <CardDescription>Recibe alertas en tiempo real en tu dispositivo</CardDescription>
            </div>
            {pushStatus.isRegistered && (
              <Badge className="text-xs bg-viable-green">Activo</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!pushStatus.isSupported ? (
            <div className="p-4 rounded-lg bg-muted/50 border border-border">
              <p className="text-sm text-muted-foreground">
                Las notificaciones push no están disponibles en este navegador. 
                Para habilitar esta función, descarga la app móvil.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="push-enabled">Habilitar notificaciones push</Label>
                  <p className="text-xs text-muted-foreground">
                    {pushStatus.permission === 'granted' 
                      ? 'Recibirás notificaciones en tiempo real' 
                      : 'Necesitas otorgar permisos para recibir notificaciones'}
                  </p>
                </div>
                <Switch
                  id="push-enabled"
                  checked={pushConfig.pushHabilitado && pushStatus.isRegistered}
                  onCheckedChange={handlePushToggle}
                />
              </div>

              {pushStatus.token && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Token de dispositivo</Label>
                  <div className="p-2 rounded bg-muted/30 border border-border">
                    <code className="text-xs break-all text-muted-foreground">
                      {pushStatus.token.substring(0, 40)}...
                    </code>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 p-3 rounded-lg bg-viable-blue/5 border border-viable-blue/20">
                <AlertCircle className="h-4 w-4 text-viable-blue" />
                <p className="text-xs text-muted-foreground">
                  Las notificaciones push funcionan mejor en la app móvil instalada
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Configuración de WhatsApp */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-viable-green/10 flex items-center justify-center">
              <MessageCircle className="h-5 w-5 text-viable-green" />
            </div>
            <div className="flex-1">
              <CardTitle>Configuración de WhatsApp</CardTitle>
              <CardDescription>Recibe notificaciones importantes por WhatsApp</CardDescription>
            </div>
            <Badge variant="outline" className="text-xs">Próximamente</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="whatsapp">Número de WhatsApp</Label>
            <Input
              id="whatsapp"
              type="tel"
              placeholder="+57 300 123 4567"
              value={whatsappConfig.numeroWhatsapp}
              onChange={(e) => setWhatsappConfig({ ...whatsappConfig, numeroWhatsapp: e.target.value })}
              disabled
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="whatsapp-enabled">Habilitar notificaciones WhatsApp</Label>
              <p className="text-xs text-muted-foreground">Activar mensajes por WhatsApp</p>
            </div>
            <Switch
              id="whatsapp-enabled"
              checked={whatsappConfig.whatsappHabilitado}
              onCheckedChange={(checked) => setWhatsappConfig({ ...whatsappConfig, whatsappHabilitado: checked })}
              disabled
            />
          </div>
        </CardContent>
      </Card>

      {/* Tipos de Notificaciones */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <Bell className="h-5 w-5 text-accent" />
            </div>
            <div>
              <CardTitle>Tipos de Notificaciones</CardTitle>
              <CardDescription>Personaliza qué notificaciones deseas recibir y por qué medios</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {categoriasUnicas.map((categoria) => (
            <div key={categoria} className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                {getCategoriaIcon(categoria)}
                <h4 className="font-semibold text-sm">{categoria}</h4>
              </div>
              
              <div className="space-y-3 pl-6 border-l-2 border-border">
                {notificaciones
                  .filter(n => n.categoria === categoria)
                  .map((notificacion) => (
                    <div key={notificacion.id} className="space-y-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-sm">{notificacion.nombre}</p>
                          <p className="text-xs text-muted-foreground">{notificacion.descripcion}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <Switch
                            checked={notificacion.email}
                            onCheckedChange={() => toggleNotificacion(notificacion.id, 'email')}
                          />
                          <Label className="text-xs text-muted-foreground">Email</Label>
                        </div>

                        <div className="flex items-center gap-2">
                          <Bell className="h-4 w-4 text-muted-foreground" />
                          <Switch
                            checked={notificacion.inApp}
                            onCheckedChange={() => toggleNotificacion(notificacion.id, 'inApp')}
                          />
                          <Label className="text-xs text-muted-foreground">In-App</Label>
                        </div>

                        <div className="flex items-center gap-2">
                          <MessageCircle className="h-4 w-4 text-muted-foreground" />
                          <Switch
                            checked={notificacion.whatsapp}
                            onCheckedChange={() => toggleNotificacion(notificacion.id, 'whatsapp')}
                            disabled
                          />
                          <Label className="text-xs text-muted-foreground">WhatsApp</Label>
                        </div>
                      </div>

                      {notificacion.diasAnticipacion !== undefined && (
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <Label className="text-xs">Anticipación:</Label>
                          <Select
                            value={notificacion.diasAnticipacion.toString()}
                            onValueChange={(value) => actualizarDiasAnticipacion(notificacion.id, parseInt(value))}
                          >
                            <SelectTrigger className="w-32 h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1 día</SelectItem>
                              <SelectItem value="3">3 días</SelectItem>
                              <SelectItem value="7">7 días</SelectItem>
                              <SelectItem value="15">15 días</SelectItem>
                              <SelectItem value="30">30 días</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Botón guardar */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => window.location.reload()}>
          Cancelar
        </Button>
        <Button onClick={guardarConfiguracion} className="bg-viable-green hover:bg-viable-green-dark">
          <CheckCircle2 className="h-4 w-4 mr-2" />
          Guardar Configuración
        </Button>
      </div>
    </div>
  );
};

export default GestionNotificaciones;
